/*
 * Created Date: Wednesday October 19th 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import "@fontsource/roboto";
import { FC, ReactElement, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Button from "./Button";
import { Upload } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";

export interface DropzoneInterface {
  allowedMaxSize?: number;
  buttonText: string;
  filesSupportedText: string;
  icon: ReactElement;
  isMultiUploadAllowed?: boolean;
  onAbort?: () => void;
  onError?: () => void;
  onLoad: (data: string | ArrayBuffer | null) => void;
  sizeSupportedText: string;
  text: string;
}

const Dropzone: FC<DropzoneInterface> = (props) => {
  const {
    allowedMaxSize,
    buttonText,
    filesSupportedText,
    icon,
    isMultiUploadAllowed,
    onAbort,
    onError,
    onLoad,
    sizeSupportedText,
    text,
  } = props;

  const onDrop = useCallback(
    (acceptedFiles: any) => {
      acceptedFiles.forEach((file: any) => {
        const reader = new FileReader();

        reader.onabort = onAbort ? onAbort : () => {};
        reader.onerror = onError ? onError : () => {};
        reader.onload = () => {
          const binaryStr = reader.result;
          onLoad(binaryStr);
        };
        reader.readAsArrayBuffer(file);
      });
    },
    [onAbort, onError, onLoad]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: allowedMaxSize,
    multiple: isMultiUploadAllowed,
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
  });

  return (
    <Stack
      {...getRootProps()}
      sx={{
        alignItems: "center",
        backgroundColor: "grey.800",
        border: "dashed",
        borderColor: "divider",
        borderRadius: 2,
        borderWidth: "thin",
        padding: 2,
      }}
    >
      <input {...getInputProps()} />
      <div style={{ paddingBottom: 5 }}>{icon}</div>
      <Typography variant="body2">{text}</Typography>
      <Typography variant="body2">{filesSupportedText}</Typography>
      <Typography variant="body2">OR</Typography>
      <Button text={buttonText} sx={{ my: 1 }} />
      <Typography variant="body2"> {sizeSupportedText}</Typography>
    </Stack>
  );
};

Dropzone.defaultProps = {
  allowedMaxSize: 10000000,
  buttonText: "Browse files",
  filesSupportedText: "File must be a .jpg or .png",
  icon: <Upload />,
  isMultiUploadAllowed: false,
  sizeSupportedText: "File must be less than 10MB",
  text: "Drag and drop your blueprint here",
};

export default Dropzone;
